/**
 * ColumnsTab - Display column configuration
 *
 * Shows source columns, aliases, transforms, and other column settings
 */

import React from 'react';
import { Columns, AlertCircle, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { View } from '@/types/views';
import { useViewData } from '@/hooks/useViews';

interface ColumnsTabProps {
  view: View;
}

/**
 * ColumnsTab Component
 */
export const ColumnsTab: React.FC<ColumnsTabProps> = ({ view }) => {
  // Backend represents "SELECT *" as an empty columns array
  // When columns is empty or undefined, the view uses SELECT * (all columns)
  // Fetch 1 row of data to infer the columns
  const { data, isLoading, error } = useViewData(
    view.id,
    { limit: 1, offset: 0 },
    { enabled: !view.columns || view.columns.length === 0 }
  );

  if (!view.columns || view.columns.length === 0) {
    // Extract column names from the fetched data
    const inferredColumns = data?.data && data.data.length > 0
      ? Object.keys(data.data[0])
      : [];

    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Columns className="h-5 w-5" />
            SELECT * (All Columns)
          </CardTitle>
          <CardDescription>
            This view selects all columns from matched files without transformation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Column schema is dynamically determined from the underlying files.
              Different files may have different columns, which will be unioned.
            </AlertDescription>
          </Alert>

          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
              <span className="ml-2 text-sm text-muted-foreground">Loading column schema...</span>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Failed to load column schema. The view may not have any data yet.
              </AlertDescription>
            </Alert>
          )}

          {!isLoading && !error && inferredColumns.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                      Column Name
                    </th>
                    <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                      Source
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {inferredColumns.map((columnName, index) => (
                    <tr key={index} className="border-b last:border-b-0">
                      <td className="py-3 px-4">
                        <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                          {columnName}
                        </code>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="text-xs">
                          Inferred from SELECT *
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isLoading && !error && inferredColumns.length === 0 && (
            <div className="text-center py-8">
              <p className="text-sm text-muted-foreground">
                No data available to infer columns. The view may not have any matched files yet.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Sort columns by order
  const sortedColumns = [...view.columns].sort((a, b) => a.column_order - b.column_order);

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Column Configuration</CardTitle>
          <CardDescription>
            Custom column selection with {sortedColumns.length} configured columns
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Columns Table */}
      <Card>
        <CardContent className="pt-6">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Order
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Source Column
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Alias
                  </th>
                  <th className="text-left py-3 px-4 font-medium text-sm text-muted-foreground">
                    Transform Expression
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedColumns.map((column) => (
                  <tr key={column.id} className="border-b last:border-b-0">
                    <td className="py-3 px-4">
                      <Badge variant="outline">{column.column_order}</Badge>
                    </td>
                    <td className="py-3 px-4">
                      <code className="text-sm font-mono bg-muted px-2 py-1 rounded">
                        {column.source_column}
                      </code>
                    </td>
                    <td className="py-3 px-4">
                      {column.column_alias ? (
                        <code className="text-sm font-mono bg-info/10 px-2 py-1 rounded">
                          {column.column_alias}
                        </code>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {column.transform_expression ? (
                        <code className="text-sm font-mono bg-warning/10 px-2 py-1 rounded block max-w-md truncate">
                          {column.transform_expression}
                        </code>
                      ) : (
                        <span className="text-muted-foreground text-sm">None</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Additional Transformations */}
      {(view.where_conditions?.length ||
        view.group_by ||
        view.having_conditions?.length ||
        view.joins?.length) && (
        <Card>
          <CardHeader>
            <CardTitle>Additional Transformations</CardTitle>
            <CardDescription>Advanced query transformations applied to this view</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {view.where_conditions && view.where_conditions.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">WHERE Conditions</div>
                <div className="space-y-2">
                  {view.where_conditions.map((condition) => (
                    <div key={condition.id} className="text-sm bg-muted px-3 py-2 rounded">
                      <code>
                        {condition.column} {condition.operator} {String(condition.value)}
                      </code>
                      {condition.logic && (
                        <Badge variant="outline" className="ml-2">
                          {condition.logic}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {view.group_by && (
              <div>
                <div className="text-sm font-medium mb-2">GROUP BY</div>
                <div className="space-y-2">
                  <div className="text-sm">
                    <span className="text-muted-foreground">Group Columns: </span>
                    <span className="font-mono">
                      {view.group_by.group_by_columns.join(', ')}
                    </span>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground mb-1">Aggregations:</div>
                    {view.group_by.aggregate_columns.map((agg) => (
                      <div key={agg.id} className="text-sm bg-muted px-3 py-2 rounded mb-1">
                        <code>
                          {agg.function}({agg.column}) AS {agg.column_alias}
                        </code>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {view.having_conditions && view.having_conditions.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">HAVING Conditions</div>
                <div className="space-y-2">
                  {view.having_conditions.map((condition) => (
                    <div key={condition.id} className="text-sm bg-muted px-3 py-2 rounded">
                      <code>
                        {condition.aggregate_column_alias} {condition.operator} {condition.value}
                      </code>
                      {condition.logic && (
                        <Badge variant="outline" className="ml-2">
                          {condition.logic}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {view.joins && view.joins.length > 0 && (
              <div>
                <div className="text-sm font-medium mb-2">JOINS</div>
                <div className="space-y-2">
                  {view.joins.map((join) => (
                    <div key={join.id} className="bg-muted px-3 py-2 rounded">
                      <div className="text-sm font-medium">
                        <Badge variant="outline" className="mr-2">
                          {join.type}
                        </Badge>
                        {join.target_view_name || join.target_view_id}
                      </div>
                      <div className="text-xs text-muted-foreground mt-1">
                        ON: {join.on_conditions.map((c) => `${c.left_column} = ${c.right_column}`).join(' AND ')}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};
